export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = "vorseum:utm";
const STORAGE_TTL_MS = 1000 * 60 * 60 * 24 * 30;

type StoredUtm = {
  values: UtmParams;
  capturedAt: number;
};

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function readUtmFromUrl(search: string): UtmParams {
  const result: UtmParams = {};
  if (!search) return result;
  const params = new URLSearchParams(search);
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      result[key] = value;
    }
  }
  return result;
}

export function persistUtm(values: UtmParams): void {
  if (!isBrowser()) return;
  if (Object.keys(values).length === 0) return;
  try {
    const payload: StoredUtm = { values, capturedAt: Date.now() };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable (private mode, quota); fail open.
  }
}

export function getStoredUtm(): UtmParams {
  if (!isBrowser()) return {};
  try {
    const raw =
      window.sessionStorage.getItem(STORAGE_KEY) ??
      window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredUtm;
    if (!parsed.capturedAt || Date.now() - parsed.capturedAt > STORAGE_TTL_MS) {
      return {};
    }
    return parsed.values ?? {};
  } catch {
    return {};
  }
}

export function appendUtmToUrl(href: string, utm: UtmParams): string {
  if (Object.keys(utm).length === 0) return href;
  try {
    const url = new URL(href, isBrowser() ? window.location.href : "https://www.vorseum.com");
    for (const key of UTM_KEYS) {
      const value = utm[key];
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    }
    return url.toString();
  } catch {
    return href;
  }
}
